import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { translations, Language, languageNames, TranslationKeys } from "@/translations";

type NestedKeyOf<T> = T extends object
  ? { [K in keyof T]: K extends string 
      ? T[K] extends object 
        ? `${K}.${NestedKeyOf<T[K]>}` 
        : K 
      : never 
    }[keyof T]
  : never;

type TranslationKey = NestedKeyOf<TranslationKeys>;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey) => string;
  languages: typeof languageNames;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const getNestedValue = (obj: any, path: string): string => {
  const keys = path.split(".");
  let result = obj;
  for (const key of keys) {
    if (result && typeof result === "object" && key in result) {
      result = result[key];
    } else {
      return path; // Return the key if translation not found
    }
  }
  return typeof result === "string" ? result : path;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("app-language") as Language;
      if (stored && translations[stored]) {
        return stored;
      }
      // Try to detect browser language
      const browserLang = navigator.language.split("-")[0] as Language;
      if (translations[browserLang]) {
        return browserLang;
      }
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("app-language", language);
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = (lang: Language) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  };

  const t = (key: TranslationKey): string => {
    return getNestedValue(translations[language], key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: languageNames }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Default fallback for when context is not available (e.g., during HMR)
const defaultLanguageContext: LanguageContextType = {
  language: "en",
  setLanguage: () => {},
  t: (key: TranslationKey) => getNestedValue(translations.en, key),
  languages: languageNames,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  // Return fallback during HMR or before provider mounts
  if (!context) {
    return defaultLanguageContext;
  }
  return context;
};
